import queryString from "query-string";
import {
  readBrowserLocation,
  setFilterValue,
  setSearchValue,
  setUiValue,
  setSortOrder,
  setPaginationValue,
} from "./appState";
import { fetchResults, setBrowserLocation, updateDisplayFields } from ".";
import { receiveResultsFailure } from "./results";
import {
  apiFieldMappings,
  failuresDisplayFields,
  historicalDisplayFields,
  uiFieldMappings,
} from "../../refdata/FilterOptions";
import { allValuesForFilter } from "../services/Filters";
import { Dispatch } from "redux";
import {
  RangeSelectFilter,
  SearchValueFilter,
  SingleValuesFilter,
  MultiSelectFilter,
  UiValue,
  PaginationFilter,
} from "../../interfaces";

export function decodeBrowserPathToState(
  store: any,
  locationSearchString: string,
) {
  return function (dispatch: Dispatch, getState: any) {
    // Abort if the browser location already matches the state
    const existingParams = store.getState().appState[getState().appState.app]
      ?.urls?.browserPath;
    if (existingParams === locationSearchString) {
      return;
    }
    // Otherwise, proceed by first parsing the string
    dispatch(readBrowserLocation());
    const params = queryString.parse(locationSearchString);
    // Process params
    // let inputFields: any;
    if (params) {
      // dispatch actions to set individual filter/query values
      setStateFromParams(store, params);
      // update the browser path in state
      store.dispatch(setBrowserLocation(locationSearchString));
      // finally, trigger the api fetch based on updated values
      store.dispatch(
        fetchResults(
          store.getState().appState[getState().appState.app],
          store.getState().config.apiPath,
        ),
      );
    } else {
      // Return error to client
      const error = new Error();
      store.dispatch(receiveResultsFailure(error));
    }
    return;
  };
}

export const isValidParam = {
  singleValue(val: any, name: string) {
    let valid = true;
    const possibleValues = allValuesForFilter(name);
    if (possibleValues && possibleValues.indexOf(val) === -1) {
      valid = false;
    }
    return valid;
  },
  multiFilter(filterVal: any, filterName: string) {
    let valid = true;
    const possibleValues = allValuesForFilter(filterName);
    const flattenedPossibleValues = [].concat.apply(
      [],
      possibleValues ? (possibleValues as never[]) : [],
    );
    const vals = filterVal.toString().split(" OR ");

    vals.forEach((val: string) => {
      if (
        flattenedPossibleValues &&
        flattenedPossibleValues.map(String).indexOf(val) === -1
      ) {
        valid = false;
      }
    });
    return valid;
  },

  displayFields(val: string) {
    if (val === undefined || val.length === 0) return false;

    const listOfDisplayFields = val.split(",");

    //identify which app display list to check

    const allDisplayFields = [
      ...historicalDisplayFields,
      ...failuresDisplayFields,
    ];

    //return false poofing fields are not present
    //return true if spoofing fields present
    let badFields = listOfDisplayFields.some(
      r => allDisplayFields.indexOf(r) < 0,
    );
    // TODO: remove console.log()
    console.error(`Invalid Fields Present: ${badFields}`);

    return !badFields;
  },
};

export const setStateFromParams = (store: any, params: any) => {
  apiFieldMappings.singleValues.forEach(
    (singleValuesFilter: SingleValuesFilter) => {
      const filterName = singleValuesFilter.filterName;
      const newValue = params[filterName];
      if (newValue && isValidParam.singleValue(newValue, filterName)) {
        store.dispatch(setFilterValue(filterName, newValue));
      }
    },
  );

  apiFieldMappings.arrayValues.forEach(
    (arrayValuesFilter: SingleValuesFilter) => {
      const filterName = arrayValuesFilter.filterName;
      const newValue = params[filterName];
      if (newValue && isValidParam.singleValue(newValue, filterName)) {
        store.dispatch(setFilterValue(filterName, newValue.split(",")));
      }
    },
  );

  if (params && isValidParam.displayFields(params.displayFields)) {
    store.dispatch(updateDisplayFields(params.displayFields.split(",")));
  } else {
    // Return error to client
    // TODO: remove console.log()
    // console.error("Query params validation failed ");
  }

  apiFieldMappings.paginationState.forEach(
    (paginationFilterValue: PaginationFilter) => {
      const filterName = paginationFilterValue.filterName;
      const newValue = params[filterName];
      if (newValue && isValidParam.singleValue(newValue, filterName)) {
        store.dispatch(setPaginationValue(filterName, parseInt(newValue)));
      }
    },
  );

  apiFieldMappings.searchValues.forEach(
    (searchValueFilter: SearchValueFilter) => {
      const searchName = searchValueFilter.searchName;
      const newValue = params[searchName];
      if (newValue && isValidParam.singleValue(newValue, searchName)) {
        store.dispatch(setSearchValue(searchName, newValue));
      }
    },
  );

  if (params.sortOrder && params.sortField) {
    store.dispatch(setSortOrder(params.sortField, params.sortOrder));
  }

  // Multi Selects (e.g. US states)
  apiFieldMappings.multiSelect.forEach(
    (multiSelectFilter: MultiSelectFilter) => {
      const filterName = multiSelectFilter.filterName;
      const newValues = params[filterName];

      if (newValues && isValidParam.multiFilter(newValues, filterName)) {
        const arrayOfVals = newValues.split(" OR ");
        store.dispatch(
          setFilterValue(
            filterName,
            arrayOfVals.map((val: any) => (isNaN(val) ? val : Number(val))),
          ),
        );
      }
    },
  );

  uiFieldMappings.singleUiValue.forEach((uiValue: UiValue) => {
    if (
      params[uiValue.uiName] &&
      isValidParam.singleValue(params[uiValue.uiName], uiValue.uiName)
    ) {
      store.dispatch(
        setUiValue(
          uiValue.uiName,
          isNaN(params[uiValue.uiName])
            ? params[uiValue.uiName] === "true" ||
              params[uiValue.uiName] === "false"
              ? JSON.parse(params[uiValue.uiName])
              : params[uiValue.uiName]
            : parseInt(params[uiValue.uiName]),
        ),
      );
    }
  });

  uiFieldMappings.multiUiValues.forEach((uiValue: UiValue) => {
    if (
      params[uiValue.uiName] &&
      isValidParam.singleValue(params[uiValue.uiName], uiValue.uiName)
    ) {
      store.dispatch(
        setUiValue(
          uiValue.uiName,
          params[uiValue.uiName].split(","),
        ),
      );
    }
  });

  apiFieldMappings.rangeSelect.forEach(
    (rangeSelectFilter: RangeSelectFilter) => {
      if (
        params[rangeSelectFilter.filterNameStart] &&
        params[rangeSelectFilter.filterNameEnd] &&
        isValidParam.singleValue(
          params[rangeSelectFilter.filterNameStart],
          rangeSelectFilter.filterNameStart,
        ) &&
        isValidParam.singleValue(
          params[rangeSelectFilter.filterNameEnd],
          rangeSelectFilter.filterNameEnd,
        )
      ) {
        store.dispatch(
          setFilterValue(
            rangeSelectFilter.filterNameStart,
            params[rangeSelectFilter.filterNameStart],
          ),
        );
        store.dispatch(
          setFilterValue(
            rangeSelectFilter.filterNameEnd,
            params[rangeSelectFilter.filterNameEnd],
          ),
        );
        if (
          rangeSelectFilter.filterDateRadio &&
          isValidParam.singleValue(
            params[rangeSelectFilter.filterDateRadio],
            rangeSelectFilter.filterDateRadio,
          )
        ) {
          store.dispatch(
            setFilterValue(
              rangeSelectFilter.filterDateRadio,
              params[rangeSelectFilter.filterDateRadio],
            ),
          );
        }
      }
      //if one range value is in place, but not the other, still decode
      else if (
        (params[rangeSelectFilter.filterNameStart]
          && !params[rangeSelectFilter.filterNameEnd])
        || (!params[rangeSelectFilter.filterNameStart]
          && params[rangeSelectFilter.filterNameEnd])
      ) {
        if (
          isValidParam.singleValue(
            params[rangeSelectFilter.filterNameStart],
            rangeSelectFilter.filterNameStart,
          ) &&
          isValidParam.singleValue(
            params[rangeSelectFilter.filterNameEnd],
            rangeSelectFilter.filterNameEnd,
          )

        ) {
          store.dispatch(
            setFilterValue(
              rangeSelectFilter.filterNameStart,
              params[rangeSelectFilter.filterNameStart],
            ),
          );
          store.dispatch(
            setFilterValue(
              rangeSelectFilter.filterNameEnd,
              params[rangeSelectFilter.filterNameEnd],
            ),
          );
        }
      }
    },
  );
};
